import { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/store/authStore';
import * as authService from '@/api/services/auth.service';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

/**
 * Gate component that protects routes behind authentication.
 *
 * 1. If the user is authenticated (has access + refresh tokens) -- render children.
 * 2. If not authenticated but a refresh token exists in storage, attempt a
 *    silent token refresh.  On success render children; on failure redirect.
 * 3. If no tokens at all, redirect to /login?returnTo=<current path>.
 */
export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const refreshToken = useAuthStore((s) => s.refreshToken);
  const hydrated = useAuthStore((s) => s._hydrated);
  const location = useLocation();

  const [isRefreshing, setIsRefreshing] = useState(false);
  const [refreshFailed, setRefreshFailed] = useState(false);

  useEffect(() => {
    // If already authenticated or no refresh token to try, skip.
    if (isAuthenticated || !refreshToken || refreshFailed) return;

    let cancelled = false;

    async function tryRefresh() {
      setIsRefreshing(true);
      try {
        const data = await authService.refreshToken(refreshToken!);
        if (!cancelled) {
          const { setTokens } = useAuthStore.getState();
          setTokens(data.access, data.refresh ?? refreshToken!);
        }
      } catch {
        if (!cancelled) {
          useAuthStore.getState().clearAuth();
          setRefreshFailed(true);
        }
      } finally {
        if (!cancelled) setIsRefreshing(false);
      }
    }

    tryRefresh();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, refreshToken, refreshFailed]);

  // Wait for zustand persist rehydration before deciding.
  if (!hydrated) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-200 border-t-primary-600" />
      </div>
    );
  }

  // While a refresh attempt is in progress, show a spinner.
  if (isRefreshing) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-200 border-t-primary-600" />
      </div>
    );
  }

  if (!isAuthenticated) {
    const returnTo = location.pathname + location.search;
    return (
      <Navigate
        to={`/login${returnTo !== '/' ? `?returnTo=${encodeURIComponent(returnTo)}` : ''}`}
        replace
      />
    );
  }

  return <>{children}</>;
}
